'use client'

import { toast } from '@/core/lib/toast'

import {
  ApiError,
  api,
  getMutationGeneration,
  primeCsrf,
} from '@/features/veluvit/services/api'
import { bootstrapEnvelopeSchema } from '@/features/veluvit/schemas/api.schema'
import { bootstrapResponseSchema } from '@/features/veluvit/schemas/bootstrap.schema'
import { APP_BOOTSTRAP_PATH } from '@/features/veluvit/lib/app-read'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Bootstrap, User } from '@/features/veluvit/types/api.types'

let sessionReader: (() => Promise<Bootstrap>) | null = null

export function connectSessionReader(read: () => Promise<Bootstrap>) {
  sessionReader = read
  return () => {
    if (sessionReader === read) sessionReader = null
  }
}

/** 화면이 사용하는 계정별 설정의 뼈대를 HTTP 경계에서 확인한다. */
export function parseBootstrap(value: unknown): Bootstrap {
  if (!bootstrapEnvelopeSchema.safeParse(value).success)
    throw new ApiError(
      '앱 정보를 확인하지 못했어요. 잠시 후 다시 불러와 주세요.',
      502,
      'INVALID_BOOTSTRAP'
    )
  return value as Bootstrap
}

/** 저장과 겹친 GET은 한 번만 재조회한다. 계속 바뀌면 오래된 응답을 버린다. */
async function readCurrentBootstrap(identity: number): Promise<Bootstrap> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const generation = getMutationGeneration()
    const value = await api<unknown>(APP_BOOTSTRAP_PATH)
    const current = useSessionStore.getState()
    if (current.identity !== identity) {
      if (current.data) return current.data
      throw new Error('계정이 바뀌어 이전 상태 조회를 종료했어요.')
    }
    if (getMutationGeneration() === generation) return parseBootstrap(value)
  }
  throw new Error('세션이 계속 변경되고 있어요. 잠시 후 다시 불러와 주세요.')
}

/** 저장 직후 별도 GET으로 갱신한다. 이전에 시작된 bootstrap GET은 공유하지 않는다. */
export async function refreshSession(): Promise<Bootstrap> {
  if (sessionReader) return sessionReader()
  const identity = useSessionStore.getState().identity
  const data = await readCurrentBootstrap(identity)
  if (useSessionStore.getState().identity !== identity) return data
  useSessionStore.getState().applyBootstrap(data)
  return data
}

/* 앱 시작 GET만 공유한다. 명시적 새로고침과 저장 직후 refreshSession은 별도 요청이다. */
let starting: {
  identity: number
  generation: number
  promise: Promise<Bootstrap>
} | null = null
export function startSession(fresh = false): Promise<Bootstrap> {
  const state = useSessionStore.getState()
  const identity = state.identity
  const generation = getMutationGeneration()
  if (!fresh && state.ready && state.data) return Promise.resolve(state.data)
  if (starting?.identity === identity && starting.generation === generation)
    return starting.promise
  const promise = readCurrentBootstrap(identity).finally(() => {
    if (starting?.promise === promise) starting = null
  })
  starting = { identity, generation, promise }
  return promise
}

let restoring: { identity: number; promise: Promise<void> } | null = null
export function restoreBrowserSession(): Promise<void> {
  const identity = useSessionStore.getState().identity
  if (restoring?.identity === identity) return restoring.promise
  const controller = new AbortController()
  const unsubscribe = useSessionStore.subscribe((state) => {
    if (state.identity !== identity) controller.abort()
  })
  const promise = api<unknown>(
    '/bootstrap/',
    undefined,
    'GET',
    controller.signal
  )
    .then((value) => {
      if (useSessionStore.getState().identity !== identity) return
      const result = bootstrapResponseSchema.safeParse(value)
      if (!result.success)
        throw new ApiError(
          '세션 복원 응답을 확인하지 못했어요.',
          502,
          'INVALID_BOOTSTRAP'
        )
      useSessionStore.getState().applyBootstrap(result.data)
    })
    .finally(() => {
      unsubscribe()
      if (restoring?.identity === identity) restoring = null
    })
  restoring = { identity, promise }
  return promise
}

/* 게스트 계정 만들기 — 동시에 여러 곳(대화방 열기·좋아요·출석…)에서 불려도 계정은 하나만 만든다.
   예전에는 겹치면 게스트가 두 개 생기고, 두 번째 요청은 서버 요청 제한(429)에 걸려 화면에 오류가 떴다 */
let creatingGuest: Promise<User> | null = null

/** 계정이 없으면 게스트로 시작한다 (레퍼런스 account() — 이름은 '하루') */
export function ensureAccount(): Promise<User> {
  const current = useSessionStore.getState().data?.user
  if (current && useSessionStore.getState().data?.sessionRestorationRequired) {
    return restoreBrowserSession().then(() => {
      const restored = useSessionStore.getState().data?.user
      if (!restored || restored.id !== current.id)
        throw new ApiError(
          '계정이 바뀌어 작업을 중단했어요.',
          0,
          'SESSION_CHANGED'
        )
      return restored
    })
  }
  if (current) return Promise.resolve(current)
  creatingGuest ??= api<unknown>('/auth/guest/', { name: '하루' })
    .then(async (value) => {
      const data = parseBootstrap(value)
      useSessionStore.getState().applyBootstrap(data)
      if (!data.user) throw new Error('계정을 만들지 못했어요.')
      await primeCsrf()
      return data.user
    })
    .finally(() => {
      creatingGuest = null
    })
  return creatingGuest
}

/** 비동기 액션을 감싸 실패 메시지를 토스트로 띄운다 (레퍼런스 safe()) */
export function safe<A extends unknown[]>(
  fn: (...args: A) => Promise<unknown> | unknown
) {
  return (...args: A) => {
    Promise.resolve()
      .then(() => fn(...args))
      .catch((error: unknown) => {
        toast.show(
          error instanceof Error && error.message
            ? error.message
            : '연결을 확인해 주세요.',
          { duration: 3200 }
        )
      })
  }
}
